import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import MenuConfig from "../../../../../constants/MenuConfig";
import { MODULE_CONFIG } from "../../../../../constants/ModuleCatalog";
import { ApiError } from "../../../Common/ApiError";
import './ModuleAccessSelector.css';


// Submenus disponíveis por módulo. Precisa refletir o menuKey usado em cada item
// de src/jsx/layouts/nav/Menu.js.
export const MENU_ITEMS_BY_MODULE = {
    DASHBOARD: [
        { menuKey: "business_overview", titleKey: "business_overview" },
        { menuKey: "birthdays", titleKey: MenuConfig.dashboard.birthdays.title },
        { menuKey: "non_returning_customers", titleKey: MenuConfig.dashboard.customer.nonReturning.title },
    ],
    REPORTS: [
        { menuKey: "hub", titleKey: MenuConfig.reports.hub.title },
        { menuKey: "employee_statement", titleKey: MenuConfig.reports.employee_statement.title },
        { menuKey: "financial_summary", titleKey: MenuConfig.reports.financial_summary.title },
        { menuKey: "pot", titleKey: MenuConfig.reports.pot.title },
        { menuKey: "extra_services", titleKey: MenuConfig.reports.extra_services.title },
    ],
    REGISTRATION: [
        { menuKey: "branch", titleKey: MenuConfig.main.branch.title },
        { menuKey: "card_flag", titleKey: MenuConfig.main.card_flag.title },
        { menuKey: "category", titleKey: MenuConfig.main.category.title },
        { menuKey: "customer", titleKey: MenuConfig.main.customer.title },
        { menuKey: "employee", titleKey: MenuConfig.main.employee.title },
        { menuKey: "vendor", titleKey: MenuConfig.main.vendor.title },
        { menuKey: "product", titleKey: MenuConfig.main.product.title },
        { menuKey: "service", titleKey: MenuConfig.main.service.title },
    ],
    APPOINTMENT: [
        { menuKey: "dashboard", titleKey: MenuConfig.appointment.dashboard.title },
        { menuKey: "appointment", titleKey: MenuConfig.appointment.appointment.title },
        { menuKey: "review", titleKey: MenuConfig.appointment.review.title },
        // A mesma chave libera Bloqueios de agenda e Datas especiais (ver Menu.js).
        { menuKey: "block", titleKey: "agenda_blocks_and_special_days" },
    ],
    STOCK_CONTROL: [
        { menuKey: "stock", titleKey: MenuConfig.stock_control.stock.title },
        { menuKey: "restock", titleKey: MenuConfig.stock_control.restock.title },
    ],
    CASH_FLOW: [
        { menuKey: "cash_registers", titleKey: MenuConfig.cash_flow.cash_registers.title },
        { menuKey: "transaction", titleKey: MenuConfig.cash_flow.transaction.title },
        { menuKey: "payable_receivable", titleKey: MenuConfig.cash_flow.payable_receivable.title },
        { menuKey: "categories", titleKey: MenuConfig.cash_flow.categories.title },
        { menuKey: "advance", titleKey: MenuConfig.cash_flow.advance.title },
        { menuKey: "employee_consumption", titleKey: MenuConfig.cash_flow.employee_consumption.title },
        { menuKey: "employee_bonus", titleKey: MenuConfig.cash_flow.employee_bonus.title },
        { menuKey: "employee_payment", titleKey: MenuConfig.cash_flow.employee_payment.title },
        { menuKey: "dre_report", titleKey: MenuConfig.cash_flow.dre_report.title },
        // Importar Dados saiu do menu lateral, entao nao entra na lista de permissoes.
    ],
    SALES: [
        { menuKey: "order", titleKey: MenuConfig.sales.order.title },
        { menuKey: "commission_rule", titleKey: MenuConfig.sales.commission_rule.title },
        { menuKey: "reason", titleKey: MenuConfig.sales.reason.title },
    ],
    PERFORMANCE: [
        { menuKey: "dashboard", titleKey: MenuConfig.performance.dashboard.title },
        { menuKey: "plan", titleKey: MenuConfig.performance.plan.title },
        { menuKey: "employee_plan", titleKey: MenuConfig.performance.employee_plan.title },
    ],
    GAMIFICATION: [
        { menuKey: "score", titleKey: MenuConfig.gamification.score.title },
        { menuKey: "pot", titleKey: MenuConfig.gamification.pot.title },
        { menuKey: "pot_bonus_rule", titleKey: MenuConfig.gamification.pot_bonus_rule.title },
    ],
    SUBSCRIPTION: [
        { menuKey: "dashboard", titleKey: MenuConfig.subscription.dashboard.title },
        { menuKey: "plan", titleKey: MenuConfig.subscription.plan.title },
        { menuKey: "subscription", titleKey: MenuConfig.subscription.subscription.title },
        { menuKey: "charge", titleKey: MenuConfig.subscription.charge.title },
        { menuKey: "club_partner", titleKey: MenuConfig.subscription.club_partner.title },
        // Assinaturas nao tem tela de relatorios no menu, entao nao gera permissao.
    ],
    WHATSAPP: [
        { menuKey: "dashboard", titleKey: MenuConfig.chat_bot.dashboard.title },
        { menuKey: "bot", titleKey: MenuConfig.chat_bot.bot.title },
        { menuKey: "conversation", titleKey: MenuConfig.chat_bot.conversation.title },
        { menuKey: "settings", titleKey: MenuConfig.chat_bot.settings.title },
    ],
};

// Ordem canônica dos cards: a de MODULE_CONFIG, com o Dashboard à frente. Sem isso a
// tela seguiria a ordem em que os módulos foram gravados para a empresa, então um
// módulo contratado depois cairia no fim da lista.
const MODULE_ORDER = Object.keys(MODULE_CONFIG);

const sortByModuleOrder = (permissions) => [...permissions].sort(
    (left, right) => MODULE_ORDER.indexOf(left) - MODULE_ORDER.indexOf(right)
);

const buildKey = (permission, menuKey) => `${permission}:${menuKey}`;

/**
 * Seletor unificado de acesso do grupo: cada módulo é uma caixa e, ao ser
 * selecionado, expande os seus submenus dentro da própria caixa. Substitui a
 * separação antiga (módulos numa seção e submenus noutra), que ficava confusa
 * com muitos módulos selecionados.
 *
 * Mantém o contrato do form: emite selectedPermissions (lista de módulos) e
 * selectedMenuPermissions (lista de { permission, menuKey }).
 */
const ModuleAccessSelector = (props) => {
    const { t } = useTranslation();
    const { initialPermissions, initialMenuPermissions, reset } = props;

    const [availablePermissions, setAvailablePermissions] = useState([]);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [selectedMenuItems, setSelectedMenuItems] = useState([]);

    const isModuleSelected = (permission) => selectedPermissions.includes(permission);
    const isMenuSelected = (permission, menuKey) => selectedMenuItems.includes(buildKey(permission, menuKey));

    const toggleModule = (permission) => {
        if (isModuleSelected(permission)) {
            // Ao desmarcar o módulo, limpamos também os submenus dele (não faz sentido
            // manter submenus de um módulo sem acesso).
            const keys = (MENU_ITEMS_BY_MODULE[permission] || []).map(i => buildKey(permission, i.menuKey));
            setSelectedPermissions(selectedPermissions.filter(p => p !== permission));
            setSelectedMenuItems(selectedMenuItems.filter(k => !keys.includes(k)));
        } else {
            setSelectedPermissions([...selectedPermissions, permission]);
            // Conceder um módulo deve liberar seus menus inicialmente; o administrador
            // ainda pode desmarcar itens específicos logo abaixo.
            const keys = (MENU_ITEMS_BY_MODULE[permission] || []).map(i => buildKey(permission, i.menuKey));
            setSelectedMenuItems([...new Set([...selectedMenuItems, ...keys])]);
        }
    };

    const toggleMenuItem = (permission, menuKey) => {
        const key = buildKey(permission, menuKey);
        if (selectedMenuItems.includes(key)) {
            setSelectedMenuItems(selectedMenuItems.filter(k => k !== key));
        } else {
            setSelectedMenuItems([...selectedMenuItems, key]);
        }
    };

    const toggleAllMenusOfModule = (permission, select) => {
        const keys = (MENU_ITEMS_BY_MODULE[permission] || []).map(i => buildKey(permission, i.menuKey));
        if (select) {
            setSelectedMenuItems([...new Set([...selectedMenuItems, ...keys])]);
        } else {
            setSelectedMenuItems(selectedMenuItems.filter(k => !keys.includes(k)));
        }
    };

    const addAllModules = () => {
        setSelectedPermissions([...availablePermissions]);
        // A ação global precisa conceder também os submenus, ou os módulos continuam
        // invisíveis para usuários que têm restrição por menu.
        const keys = availablePermissions.flatMap(permission =>
            (MENU_ITEMS_BY_MODULE[permission] || []).map(item => buildKey(permission, item.menuKey))
        );
        setSelectedMenuItems([...new Set([...selectedMenuItems, ...keys])]);
    };
    const removeAllModules = () => {
        setSelectedPermissions([]);
        setSelectedMenuItems([]);
    };

    useEffect(() => {
        if (reset !== undefined) {
            setSelectedPermissions([]);
            setSelectedMenuItems([]);
        }
        // Somente os módulos contratados pela empresa: dar a um grupo acesso a um
        // módulo que a empresa não tem só criaria permissão que não leva a lugar nenhum.
        axiosInstance.get(`${Api.identity.permission_company}`)
            .then((resp) => setAvailablePermissions(sortByModuleOrder((resp.data || []).filter(p => MODULE_CONFIG[p]))))
            .catch(error => ApiError(error));
        // eslint-disable-next-line
    }, [reset]);

    useEffect(() => {
        if (initialPermissions && initialPermissions.length > 0) {
            setSelectedPermissions(initialPermissions);
        }
        // eslint-disable-next-line
    }, [initialPermissions]);

    useEffect(() => {
        if (initialMenuPermissions && initialMenuPermissions.length > 0) {
            setSelectedMenuItems(initialMenuPermissions.map(i => buildKey(i.permission, i.menuKey)));
        }
        // eslint-disable-next-line
    }, [initialMenuPermissions]);

    useEffect(() => {
        if (props.setSelectedPermissions) {
            props.setSelectedPermissions(selectedPermissions);
        }
        // eslint-disable-next-line
    }, [selectedPermissions]);

    useEffect(() => {
        if (props.setSelectedMenuPermissions) {
            const payload = selectedMenuItems.map(key => {
                const [permission, menuKey] = key.split(":");
                return { permission, menuKey };
            });
            props.setSelectedMenuPermissions(payload);
        }
        // eslint-disable-next-line
    }, [selectedMenuItems]);

    const selectedCount = selectedPermissions.length;
    const totalCount = availablePermissions.length;

    return (
        <div className="module-access-selector">
            {/* Cabeçalho com contagem e ações globais */}
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <small className="text-muted">
                    {selectedCount} {t('of')} {totalCount} {t('modules_selected')}
                </small>
                <div className="d-flex gap-2">
                    <button
                        type="button"
                        className="btn btn-sm module-access-selector__action module-access-selector__action--add fw-semibold"
                        onClick={addAllModules}
                        disabled={selectedCount === totalCount && totalCount > 0}
                    >
                        <i className="fa-solid fa-check-double me-1"></i>
                        {t('add_all')}
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm module-access-selector__action module-access-selector__action--remove fw-semibold"
                        onClick={removeAllModules}
                        disabled={selectedCount === 0}
                    >
                        <i className="fa-solid fa-xmark me-1"></i>
                        {t('remove_all')}
                    </button>
                </div>
            </div>

            {/* Lista de módulos: cada caixa expande os próprios submenus quando selecionada */}
            <div className="d-flex flex-column gap-2">
                {availablePermissions.map((permission) => {
                    const selected = isModuleSelected(permission);
                    const config = MODULE_CONFIG[permission] || { icon: 'fa-solid fa-lock', color: '#6c757d' };
                    const label = t(`app_permissions.${permission.toLowerCase()}`);
                    const items = MENU_ITEMS_BY_MODULE[permission] || [];
                    const menuSelectedCount = items.filter(i => isMenuSelected(permission, i.menuKey)).length;

                    return (
                        <div
                            key={permission}
                            className={`card module-access-selector__module ${selected ? 'is-selected' : ''}`}
                            style={{ background: selected ? '#f0f4ff' : '#fff', transition: 'all 0.2s ease' }}
                        >
                            {/* Cabeçalho do módulo (clicável para ligar/desligar o módulo) */}
                            <div
                                className="d-flex justify-content-between align-items-center p-3"
                                style={{ cursor: 'pointer' }}
                                onClick={() => toggleModule(permission)}
                            >
                                <div className="d-flex align-items-center gap-3">
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                        style={{
                                            width: 44, height: 44,
                                            background: selected ? config.color : '#f0f0f0',
                                            transition: 'background 0.2s ease'
                                        }}
                                    >
                                        <i className={config.icon} style={{ fontSize: 18, color: selected ? '#fff' : '#bbb' }}></i>
                                    </div>
                                    <div>
                                        <span className="fw-semibold d-block" style={{ color: selected ? 'var(--bs-primary)' : '#495057' }}>
                                            {label}
                                        </span>
                                        {selected && items.length > 0 && (
                                            <small className="text-muted">
                                                {menuSelectedCount} {t('of')} {items.length} {t('menus_selected')}
                                            </small>
                                        )}
                                    </div>
                                </div>

                                <div className="d-flex align-items-center gap-2" onClick={e => e.stopPropagation()}>
                                    {selected && items.length > 0 && (
                                        <>
                                            <button
                                                type="button"
                                                className="btn btn-sm module-access-selector__action module-access-selector__action--add fw-semibold"
                                                onClick={() => toggleAllMenusOfModule(permission, true)}
                                                disabled={menuSelectedCount === items.length}
                                            >
                                                <i className="fa-solid fa-check-double me-1"></i>
                                                {t('add_all')}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm module-access-selector__action module-access-selector__action--remove fw-semibold"
                                                onClick={() => toggleAllMenusOfModule(permission, false)}
                                                disabled={menuSelectedCount === 0}
                                            >
                                                <i className="fa-solid fa-xmark me-1"></i>
                                                {t('remove_all')}
                                            </button>
                                        </>
                                    )}
                                    <div className="form-check form-switch m-0">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={selected}
                                            onChange={() => toggleModule(permission)}
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Submenus dentro da própria caixa do módulo */}
                            {selected && items.length > 0 && (
                                <div className="px-3 pb-3">
                                    <div className="border-top pt-3 d-flex flex-wrap gap-2">
                                        {items.map(item => {
                                            const checked = isMenuSelected(permission, item.menuKey);
                                            return (
                                                <div
                                                    key={item.menuKey}
                                                    // Flex simples (sem .form-check) para o checkbox ficar DENTRO da caixa:
                                                    // o .form-check padrão usa padding + margin negativa e jogava o check pra fora.
                                                    className={`module-access-selector__menu d-flex align-items-center gap-2 px-3 py-2 m-0 ${checked ? 'is-selected' : ''}`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => toggleMenuItem(permission, item.menuKey)}
                                                >
                                                    <input
                                                        className="form-check-input m-0 flex-shrink-0"
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => toggleMenuItem(permission, item.menuKey)}
                                                        onClick={e => e.stopPropagation()}
                                                    />
                                                    <label className="m-0" style={{ cursor: 'pointer' }}>
                                                        {t(item.titleKey)}
                                                    </label>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}

                {availablePermissions.length === 0 && (
                    <div className="text-center text-muted py-4">
                        <i className="fa-solid fa-circle-notch fa-spin me-2"></i>
                        {t('loading_data')}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ModuleAccessSelector;
